import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  MenuItem,
  Stack,
} from "@mui/material";
import { useToast } from "../../context/ToastContext";
import API from "../../api/axiosConfig";
import { useEffect, useState } from "react";
import ForbiddenDisplay from "../../pages/RedirectPages/AccessDeniedPage";
import { useUser } from "../../context/UserContext"; // Import your hook
import { createInvoice } from "../../utils/ExcelInvoice";
import LoadTemplateButton from "../../components/invoice/LoadTemplateButton";

export default function GenerateInvoice() {
  const showToast = useToast();

  const { isAdmin } = useUser();

  // template we're gonna use to generate the current invoice
  const [invoiceTemplate, setInvoiceTemplate] = useState(null);

  // Variables for invoice
  const [invoice, setInvoice] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [contracts, setContracts] = useState([]); // List of all contracts
  const [selectedContract, setSelectedContract] = useState("");
  const [dates, setDates] = useState([]); // List of dates for given contract
  const [selectedDate, setSelectedDate] = useState("");
  const [poNumber, setPoNumber] = useState("");

  // --- UI/UX State ---
  const [loading, setLoading] = useState(false);
  // Specific loading for the lineItems
  const [lineLoading, setLineLoading] = useState(false);

  const formatDate = (dateString) => {
    // dateString arrives as "2026-05-07"
    if (!dateString) return "";

    const [year, month, day] = dateString.split("-").map(Number);

    // We create the date object using local components
    // JS months are 0-indexed (January is 0), so subtract 1 from the month
    const date = new Date(year, month - 1, day);

    return new Intl.DateTimeFormat("en-US", {
      month: "long", // Use 'short' for "May", 'numeric' for "5", or 'long' for "May"
      year: "numeric",
    }).format(date);
  };

  const showError = (message) => {
    showToast(message, "error");
  };

  const handleInvoiceGeneration = async () => {
    // Make sure po is filled out
    if (!poNumber || poNumber.trim() === "") {
      alert("PO Number is requred to generate invoice!");
      return;
    }
    invoice.poNumber = poNumber;
    try {
      await createInvoice(invoice, invoiceTemplate);
    } catch (error) {
      console.warn(error);
      showToast("invoice generation failed", "error");
    }
  };

  // Runs on component mount
  useEffect(() => {
    // This is a function to populate the contracts selection menu
    const loadContracts = async () => {
      try {
        const response = await API.post("/api/invoice/getContracts");
        setContracts(response.data.contracts);
      } catch (err) {
        showToast("Error loading contracts", "error");
      }
    };

    const fetchData = async () => {
      setLoading(true);
      try {
        await loadContracts();
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [showToast]);

  // selectedDate watcher
  useEffect(() => {
    // Function to fetch all the lineItems from the DB
    const loadInvoice = async () => {
      if (!selectedContract || !selectedDate) {
        return;
      }
      setLineLoading(true);
      try {
        const response = await API.post("/api/invoice/getInvoice", {
          contractName: selectedContract,
          invoiceDate: selectedDate,
        });
        setInvoice(response.data);
        setLineItems(response.data.lines);
      } catch (err) {
        showToast("Error retrieving invoice lines!", "error");
        setLineItems([]);
      } finally {
        setLineLoading(false);
      }
    };

    if (selectedDate && selectedContract) {
      loadInvoice();
    } else if (!selectedDate) {
      setLineItems([]);
    }
  }, [selectedDate, selectedContract, showToast]);

  // selectedcontract watcher
  useEffect(() => {
    const fetchDates = async () => {
      if (!selectedContract) return; // Don't fetch if no contract is picked

      setLoading(true);
      try {
        const response = await API.post("/api/invoice/getDates", {
          contractName: selectedContract,
        });

        setDates(response.data);
        setSelectedDate(""); // Reset date selection when contract changes
      } catch (err) {
        showToast("Error loading contracts", "error");
      } finally {
        setLoading(false);
      }
    };
    // any time a contract is changed it shoud
    fetchDates(); // get dates for that contract
    setSelectedDate(""); // reset selectedDate
    setLineItems([]); // clear the line item field
  }, [selectedContract, showToast]);

  if (!isAdmin && !loading) {
    return <ForbiddenDisplay />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Generate Invoice
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 4,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Contract Name Selection */}
        <TextField
          select
          label="Contract Name"
          value={selectedContract}
          onChange={(e) => setSelectedContract(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {contracts.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>

        {/* Contract Date Selection */}
        <TextField
          select
          label="Invoice Date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          sx={{ minWidth: 200 }}
          disabled={!selectedContract} // Disable until a contract is picked
        >
          {dates.map((date) => (
            <MenuItem key={date} value={date}>
              {formatDate(date)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {lineLoading ? (
        <CircularProgress />
      ) : (
        <Paper elevation={3} sx={{ p: 2, maxWidth: 700, mx: "auto" }}>
          <TextField
            label="PO Number"
            variant="outlined"
            size="small"
            required // Must fill PO Number
            onChange={(e) => setPoNumber(e.target.value)}
            sx={{ width: "160px" }}
            slotProps={{
              input: {
                sx: {
                  fontSize: "1.1rem",
                  textAlign: "center",
                  mb: 3,
                },
              },
            }}
          ></TextField>

          {lineItems.map((item, index) => (
            <Box
              key={item.id || index}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
                gap: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  width: "200px",
                  textAlign: "left",
                  fontWeight: 500,
                }}
              >
                {item.employeeName}
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  width: "80px",
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                {item.hours}
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  width: "80px",
                  textAlign: "center",
                  color: "orange",
                }}
              >
                {item.overtimeHours}
              </Typography>
            </Box>
          ))}

          {lineItems.length === 0 && (
            <Typography>No line items found.</Typography>
          )}

          <Stack spacing={2} sx={{ mt: 2, alignItems: "center" }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              onClick={handleInvoiceGeneration}
              disabled={!poNumber || !invoiceTemplate}
            >
              Generate Invoice
            </Button>
            <LoadTemplateButton
              onTemplateLoaded={setInvoiceTemplate}
              onError={showError}
            />
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
